import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAuth } from '../context/AuthContext';
import {
  Alert,
  Badge,
  Loader,
  Button,
  Table,
  TableColumn,
  Title,
  Body,
  Icon,
  Dialog,
} from '../components/kern';

interface Feedback {
  id: number;
  message: string;
  sender_email: string | null;
  rating: number;
  status: string;
  url: string;
  user_agent: string;
  browser_info: string;
  has_screenshot: boolean;
  project_id: number;
  project_name: string;
  created_at: string;
  updated_at: string;
}

interface PaginatedResponse {
  feedbacks: Feedback[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function FeedbackListPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewScreenshot, setPreviewScreenshot] = useState<number | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = searchParams.get('status') || '';
  const search = searchParams.get('search') || '';

  const fetchFeedbacks = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (status) params.append('status', status);
      if (search) params.append('search', search);

      const response = await fetch(`/api/v1/feedbacks?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Feedbacks');
      }

      const data: PaginatedResponse = await response.json();
      setFeedbacks(data.feedbacks);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  }, [page, status, search, token]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Load screenshot with auth token when preview is opened
  useEffect(() => {
    if (!previewScreenshot) {
      return;
    }

    let cancelled = false;
    setScreenshotLoading(true);
    setScreenshotUrl(null);

    const loadScreenshot = async () => {
      try {
        const response = await fetch(`/api/v1/feedbacks/${previewScreenshot}/screenshot`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Screenshot konnte nicht geladen werden');
        }

        const blob = await response.blob();
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setScreenshotUrl(url);
        }
      } catch (err) {
        console.error('Screenshot load error:', err);
        if (!cancelled) {
          setScreenshotUrl(null);
        }
      } finally {
        if (!cancelled) {
          setScreenshotLoading(false);
        }
      }
    };

    loadScreenshot();

    return () => {
      cancelled = true;
    };
  }, [previewScreenshot, token]);

  const handleStatusFilter = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const searchValue = formData.get('search') as string;
    const params = new URLSearchParams(searchParams);
    if (searchValue) {
      params.set('search', searchValue);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (feedbackStatus: string) => {
    const statusMap: Record<
      string,
      { variant: 'info' | 'warning' | 'success' | 'danger'; label: string }
    > = {
      new: { variant: 'info', label: 'Neu' },
      pending: { variant: 'warning', label: 'In Bearbeitung' },
      resolved: { variant: 'success', label: 'Erledigt' },
      rejected: { variant: 'danger', label: 'Abgelehnt' },
    };
    const { variant, label } = statusMap[feedbackStatus] || statusMap.new;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const renderStars = (rating: number) => {
    return (
      <span className="rating-stars">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={`star ${i < rating ? 'star--filled' : ''}`}>
            ★
          </span>
        ))}
      </span>
    );
  };

  const feedbackColumns: TableColumn<Feedback>[] = [
    {
      key: 'id',
      header: 'ID',
      render: (feedback) => `#${feedback.id}`,
    },
    {
      key: 'message',
      header: 'Nachricht',
      render: (feedback) => (
        <div className="message-cell-content">
          <Link to={`/feedbacks/${feedback.id}`} className="kern-link message-link">
            {feedback.message.substring(0, 60)}
            {feedback.message.length > 60 ? '...' : ''}
          </Link>
          <Body size="small" muted>
            {feedback.url}
          </Body>
        </div>
      ),
    },
    {
      key: 'sender_email',
      header: 'Absender',
      render: (feedback) =>
        feedback.sender_email ? (
          <a href={`mailto:${feedback.sender_email}`} className="kern-link">
            {feedback.sender_email}
          </a>
        ) : (
          <Body size="small" muted>
            –
          </Body>
        ),
    },
    {
      key: 'rating',
      header: 'Bewertung',
      render: (feedback) => renderStars(feedback.rating),
    },
    {
      key: 'project_name',
      header: 'Projekt',
      render: (feedback) => feedback.project_name || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (feedback) => getStatusBadge(feedback.status),
    },
    {
      key: 'has_screenshot',
      header: 'Screenshot',
      render: (feedback) =>
        feedback.has_screenshot ? (
          <Button
            variant="tertiary"
            iconOnly
            icon="visibility"
            onClick={() => setPreviewScreenshot(feedback.id)}
            title="Screenshot anzeigen"
          >
            Screenshot
          </Button>
        ) : (
          '-'
        ),
    },
    {
      key: 'created_at',
      header: 'Datum',
      render: (feedback) => formatDate(feedback.created_at),
    },
    {
      key: 'actions',
      header: 'Aktionen',
      render: (feedback) => (
        <Link
          to={`/feedbacks/${feedback.id}`}
          className="kern-btn kern-btn--tertiary"
          title="Details anzeigen"
          onClick={(event) => event.stopPropagation()}
        >
          <Icon name="visibility" />
          <span className="kern-sr-only">Details anzeigen</span>
        </Link>
      ),
    },
  ];

  if (error) {
    return (
      <div className="error-container">
        <Alert variant="danger" title="Fehler">
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="feedback-list-page">
      <header className="page-header">
        <hgroup>
          <Title size="large" as="h1">
            Feedbacks
          </Title>
          <Body muted>
            {total} {total === 1 ? 'Feedback' : 'Feedbacks'} insgesamt
          </Body>
        </hgroup>
      </header>

      <div className="kern-container">
        <div className="kern-row">
          <div className="kern-col-12">
            <fieldset className="kern-fieldset">
              <form onSubmit={handleSearch}>
                <div className="kern-fieldset__body kern-fieldset__body--horizontal">
                  <div className="kern-form-input" style={{ flex: 1 }}>
                    <input
                      type="text"
                      name="search"
                      className="kern-form-input__input"
                      placeholder="Suche nach Nachricht, ..."
                      defaultValue={search}
                    />
                  </div>
                  <Button type="submit" variant="primary" icon="search" iconOnly>
                    Suchen
                  </Button>
                </div>
              </form>
            </fieldset>
          </div>
        </div>

        <div className="kern-row">
          <div className="kern-col-12">
            <fieldset className="kern-fieldset">
              <div className="kern-fieldset__body kern-fieldset__body--horizontal">
                <Button
                  variant={!status ? 'primary' : 'secondary'}
                  onClick={() => handleStatusFilter('')}
                >
                  Alle
                </Button>
                <Button
                  variant={status === 'new' ? 'primary' : 'secondary'}
                  onClick={() => handleStatusFilter('new')}
                >
                  Neu
                </Button>
                <Button
                  variant={status === 'pending' ? 'primary' : 'secondary'}
                  onClick={() => handleStatusFilter('pending')}
                >
                  In Bearbeitung
                </Button>
                <Button
                  variant={status === 'resolved' ? 'primary' : 'secondary'}
                  onClick={() => handleStatusFilter('resolved')}
                >
                  Erledigt
                </Button>
                <Button
                  variant={status === 'rejected' ? 'primary' : 'secondary'}
                  onClick={() => handleStatusFilter('rejected')}
                >
                  Abgelehnt
                </Button>
              </div>
            </fieldset>
          </div>
        </div>

        <div className="kern-row">
          <div className="kern-col-12">
            {isLoading ? (
              <div className="loading-container">
                <Loader />
                <Body>Lade Feedbacks...</Body>
              </div>
            ) : feedbacks.length > 0 ? (
              <>
                <div className="table-container">
                  <Table
                    data={feedbacks}
                    columns={feedbackColumns}
                    keyExtractor={(feedback) => feedback.id}
                    striped
                    responsive
                  />
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <Button
                      variant="secondary"
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                      icon="chevron-left"
                    >
                      Zurück
                    </Button>
                    <Body className="pagination-info">
                      Seite {page} von {totalPages}
                    </Body>
                    <Button
                      variant="secondary"
                      disabled={page >= totalPages}
                      onClick={() => handlePageChange(page + 1)}
                      icon="chevron-right"
                      iconPosition="right"
                    >
                      Weiter
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <Icon name="mail" size="x-large" />
                <Title as="h3">Keine Feedbacks gefunden</Title>
                <Body muted>
                  {search || status
                    ? 'Versuche andere Suchkriterien.'
                    : 'Feedbacks werden hier angezeigt, sobald sie eingehen.'}
                </Body>
              </div>
            )}
          </div>
        </div>
      </div>

      {previewScreenshot && (
        <Dialog
          open={!!previewScreenshot}
          onClose={() => {
            setPreviewScreenshot(null);
            if (screenshotUrl) {
              URL.revokeObjectURL(screenshotUrl);
              setScreenshotUrl(null);
            }
          }}
          title="Screenshot"
        >
          <div className="screenshot-preview">
            {screenshotLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Loader />
              </div>
            ) : screenshotUrl ? (
              <img src={screenshotUrl} alt="Screenshot" className="screenshot-preview-image" />
            ) : (
              <Body>Screenshot konnte nicht geladen werden.</Body>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
